import {
  createConnectionMonitor,
  isReachabilityFailure,
  type ConnectionState,
} from '@auctisync/api-client';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';

/**
 * Can this browser still reach the sale?
 *
 * WHY NOT `navigator.onLine`. It answers "is the radio up", and the two come
 * apart in exactly the situations that cost money: a captive portal 302s every
 * request while reporting a healthy connection, a VPN drop leaves the radio
 * perfectly happy, and our own backend being down is invisible to the browser
 * entirely. The escalation rules live in `createConnectionMonitor`, shared with
 * the phone, so both surfaces call the same drop by the same name.
 *
 * THE PROBE IS `get_server_time`, the same RPC the clock already syncs against.
 * It is the cheapest possible round trip that proves we reached OUR server, and
 * its answer doubles as the "prices as of" stamp the offline strip shows.
 *
 * `navigator.onLine` is still used, but only as an ACCELERATOR: going false is
 * treated as a failure immediately rather than waiting for the next probe, and
 * going true triggers a probe rather than declaring us online. A browser saying
 * "you have a network" is not evidence the auction is reachable.
 */
export interface ConnectionValue {
  state: ConnectionState;
  /** When we last reached the server. The moment every number on screen is true as of. */
  lastOkAt: number | null;
  /** Probe now — what the offline strip's Retry button does. */
  retry: () => void;
}

const PROBE_EVERY_MS = 15_000;
/** `reconnecting` escalates to `offline` on elapsed time, so something must ask. */
const TICK_EVERY_MS = 1_000;

const ConnectionContext = createContext<ConnectionValue | null>(null);

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const monitor = useMemo(() => createConnectionMonitor(), []);
  const [state, setState] = useState<ConnectionState>(() => monitor.snapshot().state);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  // Held in a ref so `retry` is stable and the effect below does not restart
  // its timers every time the state changes.
  const probeRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    async function probe() {
      // One probe at a time. A slow network otherwise stacks requests and the
      // failure count runs away from what actually happened.
      if (inFlight) return;
      inFlight = true;
      try {
        const { error } = await supabase.rpc('get_server_time');
        if (cancelled) return;
        // A server that ANSWERED — even to disagree — is a server we reached.
        // Only a failure to get an answer at all is a connection drop.
        if (error !== null && isReachabilityFailure(error)) {
          monitor.reportFailure();
        } else {
          monitor.reportOk();
          setLastOkAt(monitor.snapshot().lastOkAt);
        }
      } catch (err) {
        if (cancelled) return;
        if (isReachabilityFailure(err)) monitor.reportFailure();
      } finally {
        inFlight = false;
      }
    }

    probeRef.current = () => {
      void probe();
    };

    const unsubscribe = monitor.subscribe((snap) => {
      if (!cancelled) setState(snap.state);
    });

    void probe();
    const probeId = setInterval(() => void probe(), PROBE_EVERY_MS);
    const tickId = setInterval(() => {
      monitor.tick();
    }, TICK_EVERY_MS);

    const onOffline = () => {
      monitor.reportFailure();
    };
    const onOnline = () => {
      void probe();
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(probeId);
      clearInterval(tickId);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [monitor]);

  const value = useMemo<ConnectionValue>(
    () => ({
      state,
      lastOkAt,
      retry: () => probeRef.current(),
    }),
    [state, lastOkAt],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

/**
 * Connection state, or a permanently-online stand-in outside the provider.
 *
 * Deliberately not a throw: a page rendered in a test or a preview frame with
 * no provider should render its normal self, not crash. The failure mode of
 * getting this wrong is a page that never freezes, which is the same behaviour
 * the site had before the bar existed.
 */
export function useConnection(): ConnectionValue {
  return (
    useContext(ConnectionContext) ?? {
      state: 'online',
      lastOkAt: null,
      retry: () => undefined,
    }
  );
}
